import {
  SyncRuleDto,
  SyncMappingDto,
} from '../../../../services/redux/api/syncApi';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import { ILanguage } from '../../../../types/interfaces/language';
import { ExercisePropertyType } from '../../../../shared/constants';

const NAMEKEY_ENTITY_TYPES = [
  ExercisePropertyType.muscle,
  ExercisePropertyType.muscleGroup,
  ExercisePropertyType.equipment,
  ExercisePropertyType.category,
  ExercisePropertyType.developedSkill,
];

const MAPPING_ENTITY_TO_TYPE: Record<string, ExercisePropertyType> = {
  muscle: ExercisePropertyType.muscle,
  muscleGroup: ExercisePropertyType.muscleGroup,
  equipment: ExercisePropertyType.equipment,
  category: ExercisePropertyType.category,
  developedSkill: ExercisePropertyType.developedSkill,
};

function getValueByPath(obj: any, path?: string | null) {
  if (!obj || !path) return undefined;
  return path
    .split('.')
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function extractIdentifiersFromArray(arr: unknown[]): string[][] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item) => {
      if (item && typeof item === 'object') {
        const source = item as { id?: unknown; code?: unknown };
        const code = source.code == null ? '' : String(source.code).trim();
        const id = source.id == null ? '' : String(source.id).trim();
        const ordered = [code, id].filter(Boolean);
        if (!ordered.length) return null;
        return Array.from(new Set(ordered));
      }
      return null;
    })
    .filter((ids): ids is string[] => Array.isArray(ids) && ids.length > 0);
}

function applyRuleToSource(
  item: any,
  rule: SyncRuleDto,
  sourceValue?: unknown,
): string {
  let raw = sourceValue ?? getValueByPath(item?.raw ?? item, rule.sourcePath);
  if (raw == null || raw === '') {
    raw = rule.defaultValue ?? '';
  }
  if (raw == null) return '';

  let value = typeof raw === 'string' ? raw : String(raw);

  const transform = rule.transform ?? 'none';
  if (transform === 'toLowerCase') {
    value = value.toLowerCase();
  } else if (transform === 'toUpperCase') {
    value = value.toUpperCase();
  }

  if (rule.prefix) {
    value = `${rule.prefix}${value}`;
  }

  return value;
}

/**
 * Find rule by priority:
 * 1. Rule with both sourcePath and localPath
 * 2. Rule with sourcePath null/empty and defaultValue
 */
function findRuleForLocalPath(
  rules: SyncRuleDto[],
  provider: string,
  entity: string,
  localPath: string,
  preferSource: boolean,
): SyncRuleDto | undefined {
  const matching = rules.filter(
    (r) =>
      r.provider === provider &&
      r.entity === entity &&
      r.localPath === localPath,
  );
  const withSource = matching.find((r) => r.sourcePath);
  const withDefault = matching.find((r) => r.defaultValue && !r.sourcePath);

  if (preferSource && withSource) return withSource;
  if (withDefault && !withSource) return withDefault;
  return withSource ?? withDefault;
}

function resolveLocalIdByNameKey(
  nameKey: string,
  exerciseProperties: IExerciseProperty[],
  mappingEntity: string,
): string {
  const targetType = MAPPING_ENTITY_TO_TYPE[mappingEntity];
  const props = targetType
    ? exerciseProperties.filter((p) => p.type === targetType)
    : exerciseProperties;
  const found = props.find((p) => p.nameKey === nameKey);
  return found?.id ?? '';
}

function resolveLocalIdByIdOrCode(
  providerId: string,
  exerciseProperties: IExerciseProperty[],
  mappingEntity: string,
): string {
  if (!providerId) return '';
  const targetType = MAPPING_ENTITY_TO_TYPE[mappingEntity];
  const props = targetType
    ? exerciseProperties.filter((p) => p.type === targetType)
    : exerciseProperties;

  const normalizedProviderId = String(providerId);
  const found = props.find((p) => {
    const localId = String(p.id ?? '');
    const localCode = String(
      (p as IExerciseProperty & { code?: string }).code ?? '',
    );
    return (
      localId === normalizedProviderId || localCode === normalizedProviderId
    );
  });

  return found?.id ?? '';
}

/**
 * Resolves language key (e.g. "en") to language id.
 * Fallback to "en" if key not found.
 */
function resolveLanguageIdByKey(key: string, languages: ILanguage[]): string {
  if (!key || !languages.length) {
    const en = languages.find((l) => l.key === 'en');
    return en?.id ?? '';
  }
  const found = languages.find((l) => l.key === key);
  if (found) return found.id;
  const en = languages.find((l) => l.key === 'en');
  return en?.id ?? '';
}

export function applySyncRuleToValue(params: {
  item: any;
  rule?: SyncRuleDto | null;
}): string {
  const { item, rule } = params;
  if (!item || !rule) return '';
  const value = applyRuleToSource(item, rule);
  return value || '';
}

export function getApplySyncRuleValueForLocalPath(params: {
  item: any;
  rules: SyncRuleDto[];
  provider: string;
  entity: string;
  localPath: string;
}): string {
  const { item, rules, provider, entity, localPath } = params;
  const rule = findRuleForLocalPath(rules, provider, entity, localPath, true);
  if (!item || !rule) return '';
  return applyRuleToSource(item, rule);
}

/**
 * Gets array at sourcePath from item.raw, extracts ids, maps through mappings to local ids.
 * When no mapping: uses defaultValue rule (nameKey) to find local property by nameKey.
 * @param mappingEntity - entity filter for mappings (e.g. 'muscle', 'equipment', 'category')
 * @param exerciseProperties - used to resolve by nameKey when defaultValue is nameKey
 * @param defaultRuleLocalPath - optional; when mapping fails, use rule with this localPath for defaultValue (e.g. 'equipment' when source is 'types')
 */
export function getMappedIdsFromArrayPath(params: {
  item: any;
  rules: SyncRuleDto[];
  mappings: SyncMappingDto[];
  exerciseProperties?: IExerciseProperty[];
  provider: string;
  entity: string;
  localPath: string;
  mappingEntity: string;
  defaultRuleLocalPath?: string;
}): string[] {
  const {
    item,
    rules,
    mappings,
    exerciseProperties = [],
    provider,
    entity,
    localPath,
    mappingEntity,
    defaultRuleLocalPath,
  } = params;

  const ruleWithSource = findRuleForLocalPath(
    rules,
    provider,
    entity,
    localPath,
    true,
  );
  const fallbackLocalPath = defaultRuleLocalPath ?? localPath;
  const ruleWithDefault = rules.find(
    (r) =>
      r.provider === provider &&
      r.entity === entity &&
      r.localPath === fallbackLocalPath &&
      r.defaultValue &&
      !r.sourcePath,
  );

  const raw = item?.raw ?? item;
  let arr: unknown[] = [];

  if (ruleWithSource?.sourcePath) {
    const val = getValueByPath(raw, ruleWithSource.sourcePath);
    arr = Array.isArray(val) ? val : [];
  }

  if (arr.length === 0 && ruleWithDefault?.defaultValue) {
    const defaultId = resolveLocalIdByNameKey(
      ruleWithDefault.defaultValue,
      exerciseProperties,
      mappingEntity,
    );
    return defaultId ? [defaultId] : [];
  }

  if (arr.length === 0) return [];

  const providerIdentifierGroups = extractIdentifiersFromArray(arr);
  const mappingByProviderId = new Map(
    mappings
      .filter((m) => m.entity === mappingEntity && m.provider === provider)
      .map((m) => [m.providerResourceId, m.localResourceId]),
  );

  const defaultNameKey = ruleWithDefault?.defaultValue ?? '';

  return providerIdentifierGroups
    .map((identifiers) => {
      for (const identifier of identifiers) {
        const mapped = mappingByProviderId.get(identifier);
        if (mapped) return mapped;
        const directLocalId = resolveLocalIdByIdOrCode(
          identifier,
          exerciseProperties,
          mappingEntity,
        );
        if (directLocalId) return directLocalId;
      }
      if (
        defaultNameKey &&
        NAMEKEY_ENTITY_TYPES.includes(mappingEntity as ExercisePropertyType)
      ) {
        return resolveLocalIdByNameKey(
          defaultNameKey,
          exerciseProperties,
          mappingEntity,
        );
      }
      return null;
    })
    .filter((id): id is string => Boolean(id));
}

/**
 * Returns first mapped local id from array path, or empty string.
 */
export function getFirstMappedIdFromArrayPath(
  params: Parameters<typeof getMappedIdsFromArrayPath>[0],
): string {
  const ids = getMappedIdsFromArrayPath(params);
  return ids[0] ?? '';
}

/**
 * Config: sourcePath -> mappingEntity and whether first element goes to main field.
 * Used to compute exercise create prefill from rules + mappings.
 */
const EXERCISE_SOURCE_CONFIG: Record<
  string,
  { mappingEntity: string; contributesToMain: boolean }
> = {
  primaryMuscles: { mappingEntity: 'muscle', contributesToMain: true },
  secondaryMuscles: { mappingEntity: 'muscle', contributesToMain: false },
  types: { mappingEntity: 'equipment', contributesToMain: true },
  categories: { mappingEntity: 'category', contributesToMain: true },
  developedSkills: {
    mappingEntity: 'developedSkill',
    contributesToMain: false,
  },
};

/** Default rule localPath for fallback when source is empty (e.g. 'equipment' for types) */
const DEFAULT_RULE_LOCAL_PATH: Record<string, string> = {
  types: 'equipment',
  categories: 'category',
};

/**
 * Resolves provider id to local id: first in mappings (synced records),
 * then by defaultValue (nameKey) in exercise properties.
 */
function resolveProviderIdToLocal(
  providerId: string,
  mappings: SyncMappingDto[],
  mappingEntity: string,
  provider: string,
  defaultNameKey: string,
  exerciseProperties: IExerciseProperty[],
): string {
  const mapped = mappings.find(
    (m) =>
      m.entity === mappingEntity &&
      m.provider === provider &&
      m.providerResourceId === providerId,
  )?.localResourceId;
  if (mapped) return mapped;
  const directLocalId = resolveLocalIdByIdOrCode(
    providerId,
    exerciseProperties,
    mappingEntity,
  );
  if (directLocalId) return directLocalId;
  if (
    defaultNameKey &&
    NAMEKEY_ENTITY_TYPES.includes(mappingEntity as ExercisePropertyType)
  ) {
    return resolveLocalIdByNameKey(
      defaultNameKey,
      exerciseProperties,
      mappingEntity,
    );
  }
  return '';
}

/**
 * Computes exercise create prefill from item.raw using rules and mappings.
 * Rule-driven: finds rules with sourcePath, gets value from source.
 * If source is array: first element -> main field, rest -> additional.
 * Maps provider ids through synced records (mappings) to get local ids.
 */
export function computeExerciseCreatePrefill(params: {
  item: any;
  rules: SyncRuleDto[];
  mappings: SyncMappingDto[];
  exerciseProperties: IExerciseProperty[];
  languages?: ILanguage[];
  provider: string;
  entity?: string;
}): {
  title: string;
  description: string;
  categoryId: string;
  mainMuscleGroupId: string;
  equipmentId: string;
  languageId: string;
  additionalMuscleIds: string[];
  additionalEquipmentIds: string[];
  developedSkillIds: string[];
} {
  const {
    item,
    rules,
    mappings,
    exerciseProperties,
    languages = [],
    provider,
    entity = 'exercise',
  } = params;
  const raw = item?.raw ?? item;

  const result = {
    title: '',
    description: '',
    categoryId: '',
    mainMuscleGroupId: '',
    equipmentId: '',
    languageId: '',
    additionalMuscleIds: [] as string[],
    additionalEquipmentIds: [] as string[],
    developedSkillIds: [] as string[],
  };

  const exerciseRules = rules
    .filter(
      (r) => r.provider === provider && r.entity === entity && r.sourcePath,
    )
    .sort((a, b) => {
      const order = [
        'primaryMuscles',
        'secondaryMuscles',
        'types',
        'categories',
        'developedSkills',
      ];
      return order.indexOf(a.sourcePath!) - order.indexOf(b.sourcePath!);
    });

  const additionalMuscleIds: string[] = [];
  let categoryId = '';

  for (const rule of exerciseRules) {
    const config = EXERCISE_SOURCE_CONFIG[rule.sourcePath!];
    if (!config) continue;

    const val = getValueByPath(raw, rule.sourcePath);
    const arr = Array.isArray(val) ? val : val != null ? [val] : [];
    const providerIdentifierGroups = extractIdentifiersFromArray(arr);

    const ruleWithDefault = rules.find(
      (r) =>
        r.provider === provider &&
        r.entity === entity &&
        r.localPath ===
          (DEFAULT_RULE_LOCAL_PATH[rule.sourcePath!] ?? rule.localPath) &&
        r.defaultValue &&
        !r.sourcePath,
    );
    const defaultNameKey = ruleWithDefault?.defaultValue ?? '';

    const localIds = providerIdentifierGroups
      .map((identifiers) => {
        for (const identifier of identifiers) {
          const localId = resolveProviderIdToLocal(
            identifier,
            mappings,
            config.mappingEntity,
            provider,
            defaultNameKey,
            exerciseProperties,
          );
          if (localId) return localId;
        }
        return '';
      })
      .filter(Boolean);

    if (config.mappingEntity === 'muscle') {
      if (config.contributesToMain && localIds.length > 0) {
        if (!result.mainMuscleGroupId) result.mainMuscleGroupId = localIds[0];
        additionalMuscleIds.push(...localIds.slice(1));
      } else {
        additionalMuscleIds.push(...localIds);
      }
    } else if (config.mappingEntity === 'equipment') {
      if (config.contributesToMain && localIds.length > 0) {
        if (!result.equipmentId) result.equipmentId = localIds[0];
        result.additionalEquipmentIds.push(...localIds.slice(1));
      } else {
        result.additionalEquipmentIds.push(...localIds);
      }
    } else if (config.mappingEntity === 'category' && localIds.length > 0) {
      if (!categoryId) categoryId = localIds[0];
    } else if (
      config.mappingEntity === 'developedSkill' &&
      localIds.length > 0
    ) {
      result.developedSkillIds.push(...localIds);
    }
  }

  result.categoryId = categoryId;
  result.additionalMuscleIds = additionalMuscleIds;

  // Apply defaults when no value from source rules or mapping failed.
  // Default rules have localPath + defaultValue (nameKey), no sourcePath.
  const defaultRuleFields: Array<{
    localPath: string;
    resultKey: keyof typeof result;
    mappingEntity: string;
  }> = [
    {
      localPath: 'mainMuscleGroup',
      resultKey: 'mainMuscleGroupId',
      mappingEntity: 'muscle',
    },
    {
      localPath: 'equipment',
      resultKey: 'equipmentId',
      mappingEntity: 'equipment',
    },
    {
      localPath: 'category',
      resultKey: 'categoryId',
      mappingEntity: 'category',
    },
    {
      localPath: 'language',
      resultKey: 'languageId',
      mappingEntity: 'language',
    },
  ];

  for (const { localPath, resultKey, mappingEntity } of defaultRuleFields) {
    const current = result[resultKey];
    if (typeof current === 'string' && current) continue;
    const defaultRule = rules.find(
      (r) =>
        r.provider === provider &&
        r.entity === entity &&
        r.localPath === localPath &&
        r.defaultValue &&
        !r.sourcePath,
    );
    if (defaultRule?.defaultValue) {
      let defaultId = '';
      if (mappingEntity === 'language') {
        defaultId = resolveLanguageIdByKey(defaultRule.defaultValue, languages);
      } else {
        defaultId = resolveLocalIdByNameKey(
          defaultRule.defaultValue,
          exerciseProperties,
          mappingEntity,
        );
      }
      if (defaultId) {
        if (resultKey === 'mainMuscleGroupId')
          result.mainMuscleGroupId = defaultId;
        else if (resultKey === 'equipmentId') result.equipmentId = defaultId;
        else if (resultKey === 'categoryId') result.categoryId = defaultId;
        else if (resultKey === 'languageId') result.languageId = defaultId;
      }
    }
  }

  // Language: also try rule with sourcePath (value from raw), then resolve by key
  if (!result.languageId) {
    const langKey = getApplySyncRuleValueForLocalPath({
      item,
      rules,
      provider,
      entity,
      localPath: 'language',
    });
    if (langKey && languages.length > 0) {
      result.languageId = resolveLanguageIdByKey(langKey, languages);
    } else if (languages.length > 0) {
      result.languageId = resolveLanguageIdByKey('en', languages);
    }
  }

  result.title =
    getApplySyncRuleValueForLocalPath({
      item,
      rules,
      provider,
      entity,
      localPath: 'name',
    }) ||
    (raw?.name as string) ||
    '';
  result.description =
    getApplySyncRuleValueForLocalPath({
      item,
      rules,
      provider,
      entity,
      localPath: 'description',
    }) ||
    (raw?.description as string) ||
    '';

  return result;
}
